import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Drawer, Form, Button, Col, Row, Input, Select, Switch, Icon, Tabs, message, Tooltip, List, Tag } from 'antd';
import { FaStore, FaHandshake, FaStoreAlt, FaUsers, FaUserSlash, FaMoneyBillWave, FaBan } from 'react-icons/fa';
import { MdBusiness, MdBusinessCenter } from 'react-icons/md';
import styled from 'styled-components';
import uid from 'uid';
import moment from 'moment';

import { department, customerColumns } from './Utils';
import { getRuleReach, getActiveCustomers, hasRule, explainRule, describeRule, coverageLabel } from '~/_domains/tasks/enrollment';
import EnrollmentRule from './EnrollmentRule';
import { isPaymentRequired } from '~/helpers/isPaymentRequired';
import { fetch as fetchCustomerTasks } from '~/_domains/tasks/actions';

import { DatePicker } from '../UI/DatePicker/Locale';
import { AdditionalColumns, InputFields, DocumentTypesTab, SuggestAssignment } from './Tabs';
import TaskHistoryModal from './TaskHistoryModal';

var Option = Select.Option;


var SectionCard = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 16px;

  .ant-form-item { margin-bottom: 16px; }
  .ant-form-item:last-child { margin-bottom: 0; }
  .ant-form-item-label { line-height: 1.5; padding-bottom: 4px; }
  .ant-form-item-label label { font-size: 13px; font-weight: 500; color: #595959; }
  .ant-input, .ant-select-selection { border-radius: 6px; height: 40px; font-size: 14px; }
  .ant-select-selection__rendered { line-height: 38px; }
  .ant-input-affix-wrapper .ant-input { height: 40px; }
  textarea.ant-input { height: auto; }
`;

var SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

var ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child { border-bottom: none; }
`;

var ToggleInfo = styled.div`
  flex: 1;
`;

var ToggleLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #262626;
`;

var ToggleDesc = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 2px;
`;

var Columns = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 24px;

  .task-main {
    flex: 1 1 480px;
    min-width: 0;
  }
  .task-rule {
    flex: 1.3 1 560px;
    min-width: 0;
    position: sticky;
    top: 0;
    max-height: calc(100vh - 150px);
    overflow-y: auto;
    padding-right: 4px;
  }
`;

var TabCardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 0;
`;

var TabCard = styled.div`
  background: ${function(p) { return p.active ? p.activeBg || '#e6f7ff' : '#fff'; }};
  border: 1.5px solid ${function(p) { return p.active ? p.activeColor || '#1890ff' : '#f0f0f0'; }};
  border-radius: 10px 10px ${function(p) { return p.active ? '0 0' : '10px 10px'; }};
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
  position: relative;
  z-index: ${function(p) { return p.active ? 1 : 0; }};

  &:hover {
    border-color: ${function(p) { return p.activeColor || '#1890ff'; }};
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  }
`;

var TabCardIcon = styled.div`
  font-size: 22px;
  color: ${function(p) { return p.active ? p.color || '#1890ff' : '#bfbfbf'; }};
  margin-bottom: 6px;
`;

var TabCardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${function(p) { return p.active ? '#262626' : '#595959'; }};
  margin-bottom: 2px;
`;

var TabCardDesc = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  line-height: 1.4;
`;

var FooterBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  z-index: 10;

  .ant-btn {
    height: 44px;
    font-size: 15px;
    padding: 0 28px;
    border-radius: 8px;
  }
`;

var DrawerForm = function(props) {
  var form = props.form;
  var data = props.data;
  var visible = props.visible;
  var onClose = props.onClose;
  var createTask = props.createTask;
  var saveTask = props.saveTask;

  var frequencyState = useState(null);
  var frequency = frequencyState[0];
  var setFrequency = frequencyState[1];

  var fieldsState = useState([]);
  var fields = fieldsState[0];
  var setFields = fieldsState[1];

  var documentTypesState = useState([]);
  var documentTypes = documentTypesState[0];
  var setDocumentTypes = documentTypesState[1];

  var selectedTabState = useState('3');
  var selectedTab = selectedTabState[0];
  var setSelectedTab = selectedTabState[1];

  var customerColumnsState = useState([]);
  var customer_columns = customerColumnsState[0];
  var setCustomerColumns = customerColumnsState[1];

  var fileUploadState = useState(false);
  var fileUploadEnabled = fileUploadState[0];
  var setFileUploadEnabled = fileUploadState[1];

  // Exibir no módulo Conferência. Fora do form do antd de propósito: o valor é
  // sugerido a partir dos tipos de documento enquanto ninguém tocar no switch.
  var showInConferenceState = useState(false);
  var showInConference = showInConferenceState[0];
  var setShowInConference = showInConferenceState[1];

  var conferenceTouchedState = useState(false);
  var conferenceTouched = conferenceTouchedState[0];
  var setConferenceTouched = conferenceTouchedState[1];

  var historyModalState = useState(false);
  var historyModalOpen = historyModalState[0];
  var setHistoryModalOpen = historyModalState[1];

  // Regra da tarefa: para quem ela é sugerida (regime, atividade, folha). Só
  // guia — a matrícula é sempre confirmada por alguém.
  // "Não possui regra": a tarefa é escolhida cliente a cliente, de propósito.
  var enrollmentNoneState = useState(false);
  var enrollmentNone = enrollmentNoneState[0];
  var setEnrollmentNone = enrollmentNoneState[1];
  var enrollmentRegimesState = useState([]);
  var enrollmentRegimes = enrollmentRegimesState[0];
  var setEnrollmentRegimes = enrollmentRegimesState[1];

  // Recortes opcionais dentro do regime. Vazio = a tarefa serve a todos, que e
  // como as regras ja gravadas se comportam.
  var enrollmentActivitiesState = useState([]);
  var enrollmentActivities = enrollmentActivitiesState[0];
  var setEnrollmentActivities = enrollmentActivitiesState[1];

  var enrollmentPayrollState = useState([]);
  var enrollmentPayroll = enrollmentPayrollState[0];
  var setEnrollmentPayroll = enrollmentPayrollState[1];

  // Quantos clientes esta regra alcanca hoje. Escrever a regra sem esse retorno
  // era apostar: o cadastro nao dizia se o recorte pegava trinta clientes ou
  // nenhum, e so a matriz — em outra tela — contava a historia depois.
  var allCustomers = useSelector(function(state) {
    return (state.customers && state.customers.list) || [];
  });
  var enrollBaselineRaw = useSelector(function(state) {
    return (state.customerTasks && state.customerTasks.data) || {};
  });

  // Os vínculos cliente x tarefa só chegavam ao estado quando alguém abria a
  // Matriz; sem eles o alcance não separa matriculado de faltando. Busca ao
  // abrir uma tarefa existente.
  var dispatch = useDispatch();
  var editingTaskId = data && data.id ? data.id : null;
  useEffect(function() {
    if (visible && editingTaskId) {
      dispatch(fetchCustomerTasks({ showLoading: false }));
    }
  }, [visible, editingTaskId, dispatch]);

  var enrollmentReach = useMemo(function() {
    if (enrollmentRegimes.length === 0) {
      return null;
    }

    var customers = getActiveCustomers(allCustomers);
    var baselineMap = enrollBaselineRaw.customers_tasks
      ? enrollBaselineRaw.customers_tasks
      : enrollBaselineRaw;
    var taskId = data && data.id ? parseInt(data.id) : null;
    var knowsEnrollment = taskId !== null && Object.keys(baselineMap).length > 0;

    var isEnrolled = knowsEnrollment
      ? function(customer) {
          var list = baselineMap[customer.id] || [];
          return list.map(function(id) { return parseInt(id); }).indexOf(taskId) > -1;
        }
      : null;

    var fakeTask = {
      enrollment_rule: {
        regimes: enrollmentRegimes,
        activities: enrollmentActivities,
        payroll: enrollmentPayroll,
      },
    };

    var reach = getRuleReach(fakeTask, customers, isEnrolled);
    reach.knowsEnrollment = knowsEnrollment;
    // Quem está na tarefa FOLHA DE PAGAMENTO tem folha: a sugestão de regra
    // dos clientes sem regra parte disso antes de olhar o cadastro.
    var payrollTaskId = enrollBaselineRaw.payroll_task_id ? parseInt(enrollBaselineRaw.payroll_task_id) : null;
    reach.payrollOf = payrollTaskId && Object.keys(baselineMap).length > 0
      ? function(customer) {
          var list = baselineMap[customer.id] || [];
          return {
            assigned: list.map(function(id) { return parseInt(id); }).indexOf(payrollTaskId) > -1,
            employees: null,
          };
        }
      : null;
    return reach;
  }, [enrollmentRegimes, enrollmentActivities, enrollmentPayroll, allCustomers, enrollBaselineRaw, data]);

  var getFieldDecorator = form.getFieldDecorator;
  var getFieldValue = form.getFieldValue;
  var setFieldsValue = form.setFieldsValue;

  useEffect(function() {
    if (data !== null) {
      if (data.frequency) {
        setFrequency(data.frequency);
      }
      if (data.document_types) {
        setDocumentTypes(Array.isArray(data.document_types) ? data.document_types : []);
      }
      setFileUploadEnabled(!!data.file_upload);
      // Em tarefa existente o que vale é o que está salvo — nada de sugerir.
      setShowInConference(!!data.show_in_conference);
      setConferenceTouched(true);
      setEnrollmentNone(!!(data.enrollment_rule && data.enrollment_rule.none));
      setEnrollmentRegimes(
        (data.enrollment_rule && Array.isArray(data.enrollment_rule.regimes))
          ? data.enrollment_rule.regimes
          : []
      );
      setEnrollmentActivities(
        (data.enrollment_rule && Array.isArray(data.enrollment_rule.activities))
          ? data.enrollment_rule.activities
          : []
      );
      setEnrollmentPayroll(
        (data.enrollment_rule && Array.isArray(data.enrollment_rule.payroll))
          ? data.enrollment_rule.payroll
          : []
      );

      if (data.hasOwnProperty('custom_fields')) {
        var fieldsList = [];
        if (data.custom_fields !== null) {
          var customFieldsData = data.custom_fields;
          if (typeof customFieldsData === 'string') {
            try {
              customFieldsData = JSON.parse(customFieldsData);
            } catch (e) {
              customFieldsData = null;
            }
          }
          if (customFieldsData && typeof customFieldsData === 'object') {
            Object.keys(customFieldsData).map(function(row) {
              fieldsList.push({
                id: row,
                value: customFieldsData[row][0],
                type: customFieldsData[row][1],
                name: ''
              });
            });
          }
        }
        setFields(fieldsList);
        setCustomerColumns(data.customer_columns || []);
      }
    } else {
      setFrequency(null);
      setDocumentTypes([]);
      setFileUploadEnabled(false);
      setShowInConference(false);
      setConferenceTouched(false);
      setEnrollmentNone(false);
      setEnrollmentRegimes([]);
    }
  }, [data]);

  // Tarefa nova: enquanto ninguém mexer no switch, ele acompanha os tipos de
  // documento — marcar "Requer pagamento" quase sempre quer dizer guia para o
  // cliente pagar, que é o que a Conferência acompanha. Era a regra implícita
  // antiga; agora fica visível e o usuário pode discordar num clique.
  useEffect(function() {
    if (data !== null || conferenceTouched) return;

    var hasPaymentType = documentTypes.some(function(type) {
      return isPaymentRequired(type.requires_payment);
    });
    setShowInConference(hasPaymentType);
  }, [documentTypes, data, conferenceTouched]);

  var validateDocumentTypes = useCallback(function() {
    var hasDocumentTypes = documentTypes.length > 0;
    if (!hasDocumentTypes) {
      setSelectedTab('2');
      message.warning('É necessário cadastrar pelo menos um tipo de documento!');
      return false;
    }
    var areAllFilled = documentTypes.every(function(type) { return !!type.description; });
    if (!areAllFilled) {
      setSelectedTab('2');
      message.warning('Por favor, defina a descrição de todos os tipos de documento!');
      return false;
    }
    return true;
  }, [documentTypes]);

  var closeDrawer = useCallback(function() {
    onClose();
    setFields([]);
    setDocumentTypes([]);
    setShowInConference(false);
    setConferenceTouched(false);
    setSelectedTab('3');
    setHistoryModalOpen(false);
    form.resetFields();
  }, [onClose, form]);

  // Sem regime nao ha regra: os outros dois eixos sozinhos nao dizem em quem
  // matricular, so recortam dentro de um regime.
  // Filha de grupo com regra ou cobertura: a regra é a do grupo.
  var groupRules = !!(data && data.group && (hasRule(data.group) || data.group.coverage));

  var buildEnrollmentRule = useCallback(function() {
    if (enrollmentRegimes.length === 0) {
      return enrollmentNone ? { regimes: [], activities: [], payroll: [], none: true } : null;
    }
    return {
      regimes: enrollmentRegimes,
      activities: enrollmentActivities,
      payroll: enrollmentPayroll
    };
  }, [enrollmentRegimes, enrollmentActivities, enrollmentPayroll, enrollmentNone]);

  var onCreate = useCallback(function() {
    form.validateFields(function(err, values) {
      if (err) return;
      if (!!values.file_upload) {
        if (!validateDocumentTypes()) return;
      }
      createTask({
        name: values.name, department: values.department, frequency: values.frequency,
        category: values.category, competence: values.competence, description: values.description,
        first_date: values.first_date, second_date: values.second_date,
        third_date: values.third_date, fourth_date: values.fourth_date,
        file_upload: values.file_upload ? 1 : 0,
        deadline_control: values.deadline_control ? 1 : 0,
        show_in_conference: showInConference ? 1 : 0,
        document_types: documentTypes,
        customer_columns: customer_columns,
        enrollment_rule: buildEnrollmentRule()
      }).then(function() {
        form.resetFields();
        closeDrawer();
      });
    });
  }, [form, documentTypes, customer_columns, showInConference, buildEnrollmentRule, createTask, closeDrawer, validateDocumentTypes]);

  var onSave = useCallback(function() {
    if (!data || !data.id) return;
    form.validateFields(function(err, values) {
      if (err) return;
      if (!!values.file_upload) {
        if (!validateDocumentTypes()) return;
      }
      var fieldsPayload = {};
      if (fields && fields.length > 0) {
        fields.forEach(function(f) {
          fieldsPayload[f.id] = [f.value, f.type];
        });
      }
      saveTask(data.id, {
        name: values.name, department: values.department, frequency: values.frequency,
        category: values.category, competence: values.competence, description: values.description,
        first_date: values.first_date, second_date: values.second_date,
        third_date: values.third_date, fourth_date: values.fourth_date,
        file_upload: values.file_upload ? 1 : 0,
        deadline_control: values.deadline_control ? 1 : 0,
        show_in_conference: showInConference ? 1 : 0,
        fields: Object.keys(fieldsPayload).length > 0 ? fieldsPayload : null,
        document_types: documentTypes,
        customer_columns: customer_columns,
        enrollment_rule: buildEnrollmentRule()
      }).then(function() {
        form.resetFields();
        closeDrawer();
      });
    });
  }, [form, data, documentTypes, customer_columns, showInConference, buildEnrollmentRule, saveTask, closeDrawer, validateDocumentTypes]);

  var isFileUploadSelected = fileUploadEnabled;

  var ruleDatePicker = useMemo(function() {
    return { rules: [{ required: true, message: 'Escolha uma data' }] };
  }, []);

  var preserveYearOnChange = function(existingDate) {
    return function(date) {
      if (date && existingDate) {
        var newDate = date.clone();
        newDate.year(moment(existingDate).year());
        return newDate;
      }
      return date;
    };
  };

  var TAB_ITEMS = [
    { key: '3', label: 'Colunas adicionais', icon: 'table', desc: 'Exibir dados extras do cliente na listagem', color: '#1890ff', bg: '#e6f7ff' },
    { key: '1', label: 'Campos extras', icon: 'edit', desc: 'Campos personalizados para preenchimento', color: '#722ed1', bg: '#f9f0ff' },
  ];
  if (isFileUploadSelected) {
    TAB_ITEMS.push({ key: '2', label: 'Tipos de documento', icon: 'file-text', desc: 'Definir os tipos de arquivo aceitos', color: '#52c41a', bg: '#f6ffed' });
  }

  return (
    <div>
      <Drawer
        title={
          data && data.id
            ? React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: 24 } },
                React.createElement('span', null, 'Editar Tarefa'),
                React.createElement(Button, {
                  type: 'link',
                  icon: 'history',
                  onClick: function() { setHistoryModalOpen(true); },
                  style: { padding: 0, fontSize: 13 }
                }, 'Histórico')
              )
            : 'Criar Nova Tarefa'
        }
        width={Math.min(1320, window.innerWidth - 48)}
        placement="right"
        onClose={closeDrawer}
        maskClosable={false}
        visible={visible}
        destroyOnClose={true}
        zIndex={1020}
        bodyStyle={{ padding: '20px 24px 72px', background: '#fafafa' }}
      >
        <Form layout="vertical" hideRequiredMark>
        {/* Duas colunas: à esquerda a tarefa; à direita quem recebe (regra e
            sugestão), fixa enquanto a esquerda rola. Em tela estreita, empilha. */}
        <Columns>
        <div className="task-main">

          <SectionLabel>
            <Icon type="form" style={{ fontSize: 14 }} />
            Informações Básicas
          </SectionLabel>
          <SectionCard>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Tarefa">
                  {getFieldDecorator('name', {
                    initialValue: data && data.name,
                    rules: [{ required: true, message: 'Informe o nome da tarefa' }],
                  })(<Input placeholder="Nome da tarefa" />)}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Departamento">
                  {getFieldDecorator('department', data && data.id ? {
                    initialValue: data.department,
                    rules: [{ required: true, message: 'Selecione um departamento' }],
                  } : { rules: [{ required: true, message: 'Selecione um departamento' }] })(
                    <Select placeholder="Selecione">
                      <Option value="1">{department(1)}</Option>
                      <Option value="2">{department(2)}</Option>
                      <Option value="3">{department(3)}</Option>
                      <Option value="4">{department(4)}</Option>
                      <Option value="5">{department(5)}</Option>
                      <Option value="6">{department(6)}</Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Periodicidade">
                  {getFieldDecorator('frequency', data && data.id ? {
                    initialValue: data.frequency,
                    rules: [{ required: true, message: 'Selecione a periodicidade' }],
                  } : { rules: [{ required: true, message: 'Selecione a periodicidade' }] })(
                    <Select
                      placeholder="Selecione"
                      onChange={function(value) { setFrequency(value); }}
                      disabled={data && data.id !== null}>
                      <Option value={1}>Mensal</Option>
                      <Option value={2}>Trimestral</Option>
                      <Option value={3}>Semestral</Option>
                      <Option value={4}>Anual</Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Categoria">
                  {getFieldDecorator('category', {
                    initialValue: data && data.category ? data.category : undefined,
                    rules: [{ required: true, message: 'Selecione uma categoria' }],
                  })(
                    <Select placeholder="Selecione">
                      <Option value="taxes">Impostos</Option>
                      <Option value="obligations">Obrigações</Option>
                      <Option value="others">Outros</Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>

            {(frequency === 1 || frequency === 4) && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="1º Vencimento">
                    {getFieldDecorator('first_date', data && data.first_date ? {
                      initialValue: moment(data.first_date),
                      ...ruleDatePicker
                    } : ruleDatePicker)(
                      <DatePicker
                        format="DD/MM"
                        onChange={data && data.first_date ? preserveYearOnChange(data.first_date) : undefined}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            )}

            {(frequency === 2) && (
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="1º Vencimento">
                    {getFieldDecorator('first_date', data && data.first_date ? {
                      initialValue: moment(data.first_date), ...ruleDatePicker
                    } : ruleDatePicker)(
                      <DatePicker format="DD/MM" onChange={data && data.first_date ? preserveYearOnChange(data.first_date) : undefined} />
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="2º Vencimento">
                    {getFieldDecorator('second_date', data && data.second_date ? {
                      initialValue: moment(data.second_date), ...ruleDatePicker
                    } : ruleDatePicker)(
                      <DatePicker format="DD/MM" onChange={data && data.second_date ? preserveYearOnChange(data.second_date) : undefined} />
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="3º Vencimento">
                    {getFieldDecorator('third_date', data && data.third_date ? {
                      initialValue: moment(data.third_date), ...ruleDatePicker
                    } : ruleDatePicker)(
                      <DatePicker format="DD/MM" onChange={data && data.third_date ? preserveYearOnChange(data.third_date) : undefined} />
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="4º Vencimento">
                    {getFieldDecorator('fourth_date', data && data.fourth_date ? {
                      initialValue: moment(data.fourth_date), ...ruleDatePicker
                    } : ruleDatePicker)(
                      <DatePicker format="DD/MM" onChange={data && data.fourth_date ? preserveYearOnChange(data.fourth_date) : undefined} />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            )}

            {(frequency === 3) && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="1º Vencimento">
                    {getFieldDecorator('first_date', data && data.first_date ? {
                      initialValue: moment(data.first_date), ...ruleDatePicker
                    } : ruleDatePicker)(
                      <DatePicker format="DD/MM" onChange={data && data.first_date ? preserveYearOnChange(data.first_date) : undefined} />
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="2º Vencimento">
                    {getFieldDecorator('second_date', data && data.second_date ? {
                      initialValue: moment(data.second_date), ...ruleDatePicker
                    } : ruleDatePicker)(
                      <DatePicker format="DD/MM" onChange={data && data.second_date ? preserveYearOnChange(data.second_date) : undefined} />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Descrição">
                  {getFieldDecorator('description', { initialValue: data && data.description })(
                    <Input.TextArea rows={3} placeholder="Descreva detalhes sobre esta tarefa" />
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Competência">
                  {getFieldDecorator('competence', data && data.id ? {
                    initialValue: data.competence,
                    rules: [{ required: true, message: 'Selecione a competência' }],
                  } : { rules: [{ required: true, message: 'Selecione a competência' }] })(
                    <Select placeholder="Selecione">
                      <Option value="0">Igual ao vencimento</Option>
                      <Option value="-1">1 mês antes do vencimento</Option>
                      <Option value="-2">2 meses antes do vencimento</Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          <SectionLabel>
            <Icon type="setting" style={{ fontSize: 14 }} />
            Configurações
          </SectionLabel>
          <SectionCard>
            <ToggleRow>
              <Icon type="paper-clip" style={{ fontSize: 22, color: '#1890ff', flexShrink: 0 }} />
              <ToggleInfo>
                <ToggleLabel>Upload de documento</ToggleLabel>
                <ToggleDesc>Exige anexo de documento para concluir a tarefa. O colaborador precisará enviar o arquivo antes de marcar como feita.</ToggleDesc>
              </ToggleInfo>
              {getFieldDecorator('file_upload', {
                valuePropName: 'checked',
                initialValue: data && !!data.file_upload
              })(
                <Switch onChange={function(isChecked) {
                  setFileUploadEnabled(isChecked);
                  if (!isChecked && !!getFieldValue('deadline_control')) {
                    setFieldsValue({ deadline_control: false });
                  }
                }} />
              )}
            </ToggleRow>
            <ToggleRow>
              <Icon type="dashboard" style={{ fontSize: 22, color: isFileUploadSelected ? '#fa8c16' : '#d9d9d9', flexShrink: 0 }} />
              <ToggleInfo>
                <ToggleLabel style={isFileUploadSelected ? {} : { color: '#bfbfbf' }}>Controle de prazos</ToggleLabel>
                <ToggleDesc>
                  {isFileUploadSelected
                    ? 'Exibe esta tarefa no relatório de controle de prazos. Permite ao gestor acompanhar se a equipe está executando dentro do prazo.'
                    : 'Ative o upload de documento primeiro para habilitar esta opção.'
                  }
                </ToggleDesc>
              </ToggleInfo>
              {getFieldDecorator('deadline_control', {
                valuePropName: 'checked',
                initialValue: data && !!data.deadline_control
              })(
                isFileUploadSelected
                  ? <Switch />
                  : <Tooltip title="Ative o upload de documento primeiro"><Switch disabled /></Tooltip>
              )}
            </ToggleRow>
            <ToggleRow>
              <Icon type="audit" style={{ fontSize: 22, color: showInConference ? '#722ed1' : '#d9d9d9', flexShrink: 0 }} />
              <ToggleInfo>
                <ToggleLabel>Exibir na Conferência</ToggleLabel>
                <ToggleDesc>
                  Coloca esta tarefa na tela de Conferência, onde a equipe acompanha
                  cliente a cliente quem já foi avisado da guia e quem ainda falta.
                  Ligue nas tarefas que geram guia ou boleto para o cliente pagar.
                  Desligado, a tarefa continua normal no calendário e no upload de
                  documentos — só não aparece na Conferência.
                  {!conferenceTouched && showInConference && (
                    <span style={{ display: 'block', marginTop: 6, color: '#722ed1' }}>
                      Sugerido porque há tipo de documento com "Requer pagamento" ligado.
                    </span>
                  )}
                </ToggleDesc>
              </ToggleInfo>
              <Switch
                id="show_in_conference"
                checked={showInConference}
                onChange={function(isChecked) {
                  setConferenceTouched(true);
                  setShowInConference(isChecked);
                }}
              />
            </ToggleRow>
          </SectionCard>

          <SectionLabel>
            <Icon type="thunderbolt" style={{ fontSize: 14 }} />
            Quem recebe esta tarefa
          </SectionLabel>
          <SectionCard>
            {groupRules ? (
              // Filha de grupo com regra ou cobertura: quem recebe vem do grupo,
              // e a regra própria não vale — mostra a do grupo, travada.
              <div data-task-group-rule style={{ padding: '12px 14px', borderRadius: 8, background: '#fffbe6', border: '1px solid #ffe58f', fontSize: 13, lineHeight: 1.6 }}>
                <Icon type="folder" theme="filled" style={{ color: '#faad14', marginRight: 6 }} />
                Esta tarefa está no grupo <strong>{data.group.name}</strong>, e quem recebe vem do grupo:
                {' '}<strong>{hasRule(data.group) ? (explainRule(data.group.enrollment_rule) || describeRule(data.group)) : 'todos os clientes'}</strong>
                {data.group.coverage ? ' Cobertura: ' + coverageLabel(data.group.coverage).toLowerCase() + '.' : ''}
                <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>
                  Para mudar, edite o grupo em Cadastro de Tarefas → ícone de pasta. No cliente, o grupo aparece numa linha só.
                </div>
              </div>
            ) : (
            <>
            {data && data.group && (
              <div data-task-group-note style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: '#fffbe6', border: '1px solid #ffe58f', fontSize: 13 }}>
                <Icon type="folder" style={{ color: '#faad14', marginRight: 6 }} />
                Esta tarefa está no grupo livre <strong>{data.group.name}</strong>: a regra dela é esta aqui, e no cliente o grupo aparece numa linha só.
              </div>
            )}
            <EnrollmentRule
              taskId={data && data.id ? parseInt(data.id) : null}
              regimes={enrollmentRegimes}
              activities={enrollmentActivities}
              payroll={enrollmentPayroll}
              onRegimes={function(values) {
                // Escolher um regime é dar regra à tarefa: desfaz o "não possui".
                if (values.length > 0) setEnrollmentNone(false);
                setEnrollmentRegimes(values);
              }}
              none={enrollmentNone}
              onNone={function(value) {
                setEnrollmentNone(value);
                if (value) {
                  setEnrollmentRegimes([]);
                  setEnrollmentActivities([]);
                  setEnrollmentPayroll([]);
                }
              }}
              onActivities={setEnrollmentActivities}
              onPayroll={setEnrollmentPayroll}
              reach={enrollmentReach}
              part="rule"
            />
            </>
            )}
          </SectionCard>

          <SectionLabel>
            <Icon type="appstore" style={{ fontSize: 14 }} />
            Personalizações
          </SectionLabel>

          <TabCardsRow>
            {TAB_ITEMS.map(function(t) {
              var isActive = selectedTab === t.key;
              return React.createElement(TabCard, {
                key: t.key,
                active: isActive,
                activeColor: t.color,
                activeBg: t.bg,
                onClick: function() { setSelectedTab(t.key); }
              },
                React.createElement(TabCardIcon, { active: isActive, color: t.color },
                  React.createElement(Icon, { type: t.icon })
                ),
                React.createElement(TabCardTitle, { active: isActive }, t.label),
                React.createElement(TabCardDesc, null, t.desc)
              );
            })}
          </TabCardsRow>

          <SectionCard style={{ borderRadius: '0 0 10px 10px', borderTop: 'none', marginTop: 0 }}>
            {selectedTab === '3' && (
              <AdditionalColumns
                customerColumns={customerColumns}
                customer_columns={customer_columns}
                setCustomerColumns={setCustomerColumns}
              />
            )}
            {selectedTab === '1' && (
              <InputFields
                fields={fields}
                setFields={setFields}
                form={form}
              />
            )}
            {selectedTab === '2' && isFileUploadSelected && (
              <DocumentTypesTab
                types={documentTypes}
                setState={setDocumentTypes}
              />
            )}
          </SectionCard>
        </div>
        <div className="task-rule" data-task-rule-column>
          <SectionLabel>
            <Icon type="team" style={{ fontSize: 14 }} />
            Clientes: o que fazer
          </SectionLabel>
          <SectionCard>
            {groupRules ? (
              <div style={{ fontSize: 13, color: '#595959', lineHeight: 1.6 }}>
                Esta tarefa segue o grupo <strong>{data.group.name}</strong>: a sugestão e as pendências dos clientes
                aparecem na aba Regra de cada cliente, numa linha do grupo.
              </div>
            ) : (
              <EnrollmentRule
                taskId={data && data.id ? parseInt(data.id) : null}
                regimes={enrollmentRegimes}
                activities={enrollmentActivities}
                payroll={enrollmentPayroll}
                onRegimes={setEnrollmentRegimes}
                onActivities={setEnrollmentActivities}
                onPayroll={setEnrollmentPayroll}
                reach={enrollmentReach}
                none={enrollmentNone}
                part="result"
              />
            )}
          </SectionCard>
        </div>
        </Columns>

        </Form>

        <FooterBar>
          <Button onClick={closeDrawer}>
            Cancelar
          </Button>
          {data && data.id ? (
            <Button onClick={onSave} type="primary" icon="save">Salvar</Button>
          ) : (
            <Button onClick={onCreate} type="primary" icon="plus">Cadastrar</Button>
          )}
        </FooterBar>
      </Drawer>
      <TaskHistoryModal
        isOpen={historyModalOpen}
        closeModal={function() { setHistoryModalOpen(false); }}
        taskId={data && data.id}
      />
    </div>
  );
};

var mapStateToProps = function() { return {}; };

var mapDispatchProps = function(dispatch) {
  return {
    createTask: function(data) {
      return dispatch({
        type: 'CREATE_TASK',
        payload: {
          request: { method: 'post', url: '/tasks', data: data }
        }
      });
    },
    saveTask: function(id, data) {
      return dispatch({
        type: 'SAVE_TASK',
        payload: {
          request: { method: 'post', url: '/tasks/' + id, data: data }
        }
      });
    },
  };
};

export default connect(mapStateToProps, mapDispatchProps)(Form.create()(DrawerForm));
